import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";
import {
  REMOTE_SOURCE,
  type AdapterConfig,
  type ConfigAckPayload,
  type ShellToRemoteMessage,
} from "@claudafinil/shared/contract";
import { useAuthSessionStore } from "../features/auth";
import { setCloudAnnotationsApiClient } from "../features/annotations";
import { setTeamsApiClient, useCloudPromptsStore } from "../features/prompts";
import { useTimelineStore } from "../features/timeline";
import { getAdapterConfigForPlatform } from "../lib/adapter-config-catalog";
import { createRuntimeApiClient } from "../lib/api-runtime";
import { createShellCommands, type ShellCommands } from "../lib/shell-commands";
import {
  createShellMessenger,
  type ConnectionState,
  type ShellMessenger,
} from "../lib/shell-messenger";

export type AdapterPlatform = AdapterConfig["platform"];
type AdapterConfigState = "publishing" | "applied" | "rejected" | "error";

// Claude.ai hydrates the conversation server-side on full page reload and
// never fires the `?tree=True` request our interceptor watches for, so the
// tree would stay empty. Drive the fetch ourselves once the shell connection
// is up. `isStale` short-circuits if the messenger has been replaced (e.g.
// reconnect) while the network request was in flight.
async function loadInitialConversation(
  commands: ShellCommands,
  isStale: () => boolean,
): Promise<void> {
  const store = useTimelineStore.getState();
  store.setLoading(true);
  store.setError(null);

  try {
    const result = await commands.refreshConversation();
    if (isStale()) return;
    store.setLoading(false);
    store.setError(result.success ? null : result.error ?? null);
  } catch (error) {
    if (isStale()) return;
    store.setLoading(false);
    store.setError(
      error instanceof Error ? error.message : "Failed to load conversation.",
    );
  }
}

export interface AdapterConfigStatus {
  state: AdapterConfigState;
  platform: AdapterPlatform;
  version: string;
  detail?: string;
}

interface UseShellConnectionResult {
  connect: () => void;
  connectionState: ConnectionState;
  selectedVersion: number | null;
  extensionVersion: string | null;
  adapterConfigStatus: AdapterConfigStatus | null;
  commandsRef: MutableRefObject<ShellCommands | null>;
  runtimeApiClientRef: MutableRefObject<ReturnType<typeof createRuntimeApiClient> | null>;
}

export function useShellConnection(): UseShellConnectionResult {
  const messengerRef = useRef<ShellMessenger | null>(null);
  const commandsRef = useRef<ShellCommands | null>(null);
  const runtimeApiClientRef = useRef<ReturnType<typeof createRuntimeApiClient> | null>(
    null,
  );
  const publishGenerationRef = useRef(0);
  const latestPublishedConfigRef = useRef<{
    platform: AdapterPlatform;
    version: string;
  } | null>(null);

  const [connectionState, setConnectionState] =
    useState<ConnectionState>("connecting");
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [extensionVersion, setExtensionVersion] = useState<string | null>(null);
  const [activePlatform, setActivePlatform] = useState<AdapterPlatform | null>(
    null,
  );
  const [adapterConfigStatus, setAdapterConfigStatus] =
    useState<AdapterConfigStatus | null>(null);

  const publishAdapterConfig = useCallback(
    async (platform: AdapterPlatform): Promise<void> => {
      const messenger = messengerRef.current;
      if (!messenger) {
        return;
      }

      const config = getAdapterConfigForPlatform(platform);
      const generation = publishGenerationRef.current + 1;
      publishGenerationRef.current = generation;
      latestPublishedConfigRef.current = {
        platform,
        version: config.version,
      };
      setAdapterConfigStatus({
        state: "publishing",
        platform,
        version: config.version,
      });

      try {
        const ack = await messenger.sendRequest<
          ConfigAckPayload,
          "ADAPTER_CONFIG"
        >("ADAPTER_CONFIG", config);
        if (
          messengerRef.current !== messenger ||
          publishGenerationRef.current !== generation
        ) {
          return;
        }

        setAdapterConfigStatus({
          state: ack.applied ? "applied" : "rejected",
          platform,
          version: config.version,
          detail: ack.applied
            ? undefined
            : `Adapter config: rejected ${platform}@${config.version}; shell fallback remains active.`,
        });
      } catch (error) {
        if (
          messengerRef.current !== messenger ||
          publishGenerationRef.current !== generation
        ) {
          return;
        }

        setAdapterConfigStatus({
          state: "error",
          platform,
          version: config.version,
          detail:
            error instanceof Error
              ? `Adapter config publish failed: ${error.message}`
              : "Adapter config publish failed.",
        });
      }
    },
    [],
  );

  useEffect(() => {
    if (connectionState !== "connected" || activePlatform === null) {
      return;
    }

    void publishAdapterConfig(activePlatform);
  }, [activePlatform, connectionState, publishAdapterConfig]);

  const handleShellMessage = useCallback((message: ShellToRemoteMessage) => {
    if (message.type === "HANDSHAKE_ACK") {
      if (message.payload.platform) {
        setActivePlatform(message.payload.platform);
        useTimelineStore.getState().setActivePlatform(message.payload.platform);
      }
      return;
    }

    if (message.type === "CONFIG_ACK") {
      const latestPublished = latestPublishedConfigRef.current;
      if (!latestPublished) {
        return;
      }

      setAdapterConfigStatus((current) => {
        if (!current) {
          return current;
        }

        if (
          current.platform !== latestPublished.platform ||
          current.version !== latestPublished.version
        ) {
          return current;
        }

        if (message.payload.applied) {
          if (current.state === "publishing") {
            return {
              ...current,
              state: "applied",
              detail: undefined,
            };
          }
          return current;
        }

        if (current.state === "rejected" || current.state === "error") {
          return current;
        }

        return {
          ...current,
          state: "rejected",
          detail: `Adapter config: rejected ${current.platform}@${current.version}; shell fallback remains active.`,
        };
      });
      return;
    }

    if (message.type === "CONVERSATION_DATA") {
      useTimelineStore.getState().setConversationData(message.payload);
      return;
    }

    if (message.type === "CURRENT_LEAF_UPDATED") {
      useTimelineStore.getState().updateCurrentLeaf(message.payload.leafId);
      return;
    }

    if (message.type === "AUTH_STATE") {
      useAuthSessionStore.getState().setAuthState(message.payload);
      if (!message.payload.isAuthenticated) {
        useAuthSessionStore.getState().setAccessToken(null);
        return;
      }

      if (!useAuthSessionStore.getState().accessToken) {
        void commandsRef.current
          ?.requestAuthToken()
          .then((accessToken) => {
            useAuthSessionStore.getState().setAccessToken(accessToken);
          })
          .catch(() => undefined);
      }
      return;
    }

    if (message.type === "AUTH_TOKEN") {
      useAuthSessionStore
        .getState()
        .setAccessToken(message.payload.accessToken);
    }
  }, []);

  const connect = useCallback(() => {
    const messenger = messengerRef.current;
    if (!messenger) {
      return;
    }

    useTimelineStore.getState().clearConversation();
    useTimelineStore.getState().setActivePlatform(null);
    useAuthSessionStore.getState().reset();
    setSelectedVersion(null);
    setExtensionVersion(null);
    setActivePlatform(null);
    setAdapterConfigStatus(null);
    publishGenerationRef.current = 0;
    latestPublishedConfigRef.current = null;
    setConnectionState("connecting");

    void messenger.connect().catch(() => undefined);
    void messenger.connectionReady
      .then(({ version, platform, extensionVersion: nextExtensionVersion }) => {
        if (messengerRef.current !== messenger) return;

        setSelectedVersion(version);
        setExtensionVersion(nextExtensionVersion ?? null);
        const nextPlatform = platform ?? "claude";
        setActivePlatform(nextPlatform);
        useTimelineStore.getState().setActivePlatform(nextPlatform);
        setConnectionState("connected");
        messenger.sendToShell({
          source: REMOTE_SOURCE,
          type: "READY",
          version,
          payload: {},
        });

        if (nextPlatform === "claude" && commandsRef.current) {
          void loadInitialConversation(
            commandsRef.current,
            () => messengerRef.current !== messenger,
          );
        }
      })
      .catch(() => {
        if (messengerRef.current !== messenger) return;
        setConnectionState(messenger.connectionState);
      });
  }, []);

  useEffect(() => {
    const messenger = createShellMessenger();
    const commands = createShellCommands(messenger);
    const runtimeApiClient = createRuntimeApiClient(commands);

    commandsRef.current = commands;
    runtimeApiClientRef.current = runtimeApiClient;
    useCloudPromptsStore.getState().setApiClient(runtimeApiClient);
    setCloudAnnotationsApiClient(runtimeApiClient);
    setTeamsApiClient(runtimeApiClient);
    messengerRef.current = messenger;

    const unsubscribe = messenger.onShellMessage(handleShellMessage);
    connect();

    return () => {
      unsubscribe();
      if (messengerRef.current === messenger) {
        messengerRef.current = null;
      }
      if (commandsRef.current === commands) {
        commandsRef.current = null;
      }
      if (runtimeApiClientRef.current === runtimeApiClient) {
        runtimeApiClientRef.current = null;
      }
      useCloudPromptsStore.getState().setApiClient(null);
      setCloudAnnotationsApiClient(null);
      setTeamsApiClient(null);
      messenger.destroy();
    };
  }, [connect, handleShellMessage]);

  return {
    connect,
    connectionState,
    selectedVersion,
    extensionVersion,
    adapterConfigStatus,
    commandsRef,
    runtimeApiClientRef,
  };
}
